import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';

export const useQuery = () => {
  // Get the current location (search, pathname, and hash) and navigation function
  const { search, pathname, hash } = useLocation();
  const navigate = useNavigate();

  // Create a memoized URLSearchParams instance based on the current search string
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);

  // Update a specific search parameter while preserving existing ones
  const updateSearchParams = (key: string, value: string) => {
    // Create a new URLSearchParams instance from the current search to preserve all existing parameters
    const newParams = new URLSearchParams(search);
    // Set the new or updated key-value pair (updates if key exists, adds if new)
    newParams.set(key, value);
    // Navigate to the updated URL with all parameters, preserving the current hash
    navigate({
      pathname,
      search: `?${newParams.toString()}`,
      hash,
    });
  };

  // Update the URL with an object of parameters, preserving existing ones
  const updateURLWithObject = (obj: Record<string, any>) => {
    const newParams = new URLSearchParams(search); // Create new instance from current search
    Object.entries(obj).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        newParams.set(key, String(value)); // Add or update parameter if value is not undefined, null, or empty string
      } else {
        newParams.delete(key); // Remove parameter if value is undefined, null, or empty string
      }
    });
    navigate({
      pathname,
      search: `?${newParams.toString()}`, // Navigate to the new URL
      hash, // Preserve the current hash
    });
  };

  // Remove a specific search parameter
  const removeSearchParams = (
    key: string,
    options: { replace?: boolean } = {},
  ) => {
    const newParams = new URLSearchParams(search); // Create new instance from current search
    newParams.delete(key); // Delete the parameter
    navigate(
      {
        pathname,
        search: `?${newParams.toString()}`, // Navigate to the new URL
        hash, // Preserve the current hash
      },
      { replace: options.replace ?? false },
    );
  };

  const removeSearchParamsByList = (keysToRemove: string[]) => {
    const newParams = new URLSearchParams(search); // Create a new instance of the current params
    keysToRemove.forEach(key => newParams.delete(key)); // Remove each key in the array
    navigate({
      pathname,
      search: `?${newParams.toString()}`, // Navigate to the new URL
      hash, // Preserve the current hash
    });
  };

  const resetSearchParams = () => {
    navigate({
      pathname,
      search: '', // Navigate to the URL without any query parameters
      // hash is intentionally NOT preserved on full reset
    });
  };

  // Get the value of a specific search parameter
  const getSearchParams = (key: string) => searchParams.get(key);

  // Return the searchParams object and utility functions
  return {
    searchParams,
    getSearchParams,
    updateSearchParams,
    removeSearchParams,
    updateURLWithObject,
    removeSearchParamsByList,
    resetSearchParams,
  };
};
